// Subscription and pricing types for A Daily Review

import type { PermissionLevel } from './admin';

export type SubscriptionTier = 'foundation' | 'deep_dive' | 'mastermind';

export type SubscriptionStatus =
  | 'active'
  | 'canceled'
  | 'past_due'
  | 'incomplete'
  | 'trialing';

export interface Subscription {
  id: string;
  user_id: string;
  stripe_subscription_id: string | null;
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserProfile {
  id: string;
  user_id: string;
  total_saved_quizzes: number;
  stripe_customer_id: string | null;
  permission_level: PermissionLevel;
  display_name: string | null;
  first_name: string | null;
  last_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface MonthlyQuizUsage {
  id: string;
  user_id: string;
  year_month: string; // Format: YYYY-MM
  quiz_count: number;
  created_at: string;
  updated_at: string;
}

// Legacy alias for backwards compatibility
export type DailyQuizUsage = MonthlyQuizUsage;

export interface SubscriptionHistory {
  id: string;
  subscription_id: string | null;
  user_id: string;
  event_type: 'created' | 'upgraded' | 'downgraded' | 'canceled' | 'renewed' | 'payment_failed';
  from_tier: SubscriptionTier | null;
  to_tier: SubscriptionTier | null;
  stripe_event_id: string | null;
  created_at: string;
}

export interface TierLimits {
  tier: SubscriptionTier;
  monthlyQuizLimit: number;
  savedQuizzesLimit: number | null; // null = unlimited
  price: number; // in dollars
  priceId: string; // Stripe Price ID
}

// Frontend tier configuration (without Stripe Price IDs for security)
export const TIER_LIMITS: Record<SubscriptionTier, Omit<TierLimits, 'priceId'>> = {
  foundation: {
    tier: 'foundation',
    monthlyQuizLimit: 10,
    savedQuizzesLimit: 30,
    price: 0,
  },
  deep_dive: {
    tier: 'deep_dive',
    monthlyQuizLimit: 50,
    savedQuizzesLimit: 300,
    price: 4.99,
  },
  mastermind: {
    tier: 'mastermind',
    monthlyQuizLimit: 100,
    savedQuizzesLimit: null, // unlimited
    price: 7.99,
  },
};

// Tier display names
export const TIER_NAMES: Record<SubscriptionTier, string> = {
  foundation: 'Foundation',
  deep_dive: 'Deep Dive',
  mastermind: 'Mastermind',
};

// Helper function to get tier limits
export function getTierLimits(tier: SubscriptionTier): Omit<TierLimits, 'priceId'> {
  return TIER_LIMITS[tier];
}

// Helper function to check if user can generate more quizzes
export function canGenerateQuiz(usage: MonthlyQuizUsage, tier: SubscriptionTier): boolean {
  const limits = getTierLimits(tier);
  return usage.quiz_count < limits.monthlyQuizLimit;
}

// Helper function to check if user can save more quizzes
export function canSaveQuiz(profile: UserProfile, tier: SubscriptionTier): boolean {
  const limits = getTierLimits(tier);
  if (limits.savedQuizzesLimit === null) {
    return true; // unlimited
  }
  return profile.total_saved_quizzes < limits.savedQuizzesLimit;
}

// Usage status interface
export interface UsageStatus {
  allowed: boolean;
  current: number;
  limit: number;
  remainingThisMonth?: number;
}

// Checkout session request
export interface CreateCheckoutSessionRequest {
  tier: SubscriptionTier;
}

// Checkout session response (embedded checkout)
export interface CreateCheckoutSessionResponse {
  clientSecret: string;
}

// Customer portal session response
export interface CreatePortalSessionResponse {
  url: string;
}
